import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import {
    Code,
    Server,
    ExternalLink,
    Layers,
    Monitor,
    ArrowUpRight,
    Globe,
    Users,
    Mail,
    Info,
} from "lucide-react";
import { SiLinkedin } from "react-icons/si";
import { useToast } from "../../../hooks/use-toast";
import { useEffect, useState } from "react";
import axios from "axios";
import { NGROK } from "../../../APIs";
import { FaApple, FaLinux, FaWindows } from "react-icons/fa6";
import ProductBrandCard from "../ProductInfo/ProductBrandCard";
import ProductVersion from "../ProductInfo/ProductVersion";
import ProductInfoGuide from "../ProductInfo/ProductInfoGuide";

export default function AboutPage() {
    const { toast } = useToast();
    const [backendVersion, setBackendVersion] = useState<string>("N/A");
    const [windowsVersion, setWindowsVersion] = useState<string>("N/A");
    const [linuxVersion, setLinuxVersion] = useState<string>("N/A");
    const [macVersion, setMacVersion] = useState<string>("N/A");
    const [versionLoading, setVersionLoading] = useState({
        backend: true,
        windows: true,
        linux: true,
        mac: true,
    });

    const fetchAgentVersion = async (os: "windows" | "linux" | "mac") => {
        try {
            const response = await axios.get(
                `${NGROK}/api/agent/${os}-agent-version`,
            );
            const version = response?.data ?? "N/A";
            switch (os) {
                case "windows":
                    setWindowsVersion(version);
                    break;
                case "linux":
                    setLinuxVersion(version);
                    break;
                case "mac":
                    setMacVersion(version);
                    break;
            }
        } catch (error) {
            switch (os) {
                case "windows":
                    setWindowsVersion("N/A");
                    break;
                case "linux":
                    setLinuxVersion("N/A");
                    break;
                case "mac":
                    setMacVersion("N/A");
                    break;
            }
            console.error(`Failed to fetch ${os} agent version`, error);
        } finally {
            setVersionLoading((previous) => ({ ...previous, [os]: false }));
        }
    };

    const fetchBackendVersion = async () => {
        try {
            const response = await axios.get(`${NGROK}/api/build-version`);
            setBackendVersion(response?.data ?? "N/A");
        } catch (error) {
            setBackendVersion("N/A");
            console.error("Failed to fetch backend version", error);
        } finally {
            setVersionLoading((previous) => ({ ...previous, backend: false }));
        }
    };

    useEffect(() => {
        fetchBackendVersion();
        fetchAgentVersion("windows");
        fetchAgentVersion("linux");
        fetchAgentVersion("mac");
    }, []);

    const handleLinkClick = (linkName: string, url?: string) => {
        if (url) window.open(url, "_blank", "noopener,noreferrer");
        toast({ title: "Link Opened", description: `Opening ${linkName}...` });
    };

    const platformVersions = [
        {
            id: "frontend",
            label: "Web console",
            version: "5.3.126",
            icon: Code,
            description: "WhiteSwan management interface",
            loading: false,
        },
        {
            id: "backend",
            label: "Backend",
            version: backendVersion,
            icon: Server,
            description: "Platform services",
            loading: versionLoading.backend,
        },
    ];

    const agentVersions = [
        {
            id: "windows",
            label: "Windows",
            version: windowsVersion,
            icon: FaWindows,
            description: "Windows agent service",
            loading: versionLoading.windows,
        },
        {
            id: "mac",
            label: "Mac",
            version: macVersion,
            icon: FaApple,
            description: "macOS agent service",
            loading: versionLoading.mac,
        },
        {
            id: "linux",
            label: "Linux",
            version: linuxVersion,
            icon: FaLinux,
            description: "Linux daemon service",
            loading: versionLoading.linux,
        },
    ];

    const companyLinks = [
        {
            id: "whiteswan",
            label: "WhiteSwan",
            description: "Explore our website",
            icon: Globe,
            url: "https://www.whiteswansecurity.com/",
        },
        {
            id: "about",
            label: "About Us",
            description: "Meet the company",
            icon: Users,
            url: "https://www.whiteswansecurity.com/about/",
        },
        {
            id: "contact",
            label: "Contact Us",
            description: "Get in touch",
            icon: Mail,
            url: "https://www.whiteswansecurity.com/contact/",
        },
        {
            id: "linkedin",
            label: "LinkedIn",
            description: "Follow our updates",
            icon: SiLinkedin,
            url: "https://www.linkedin.com/company/whiteswan-identity-security/",
        },
    ];

    return (
        <div className="product-info-page about-page">
            <ProductBrandCard
                title="About WhiteSwan"
                description="Platform and agent version information for your WhiteSwanSecurity deployment."
            />
            <div className="product-version-grid">
                <Card className="product-panel">
                    <CardContent className="product-panel-content">
                        <div className="product-section-heading">
                            <span className="product-icon">
                                <Layers />
                            </span>
                            <div>
                                <h2>Platform versions</h2>
                                <p>The console and services in your deployment.</p>
                            </div>
                        </div>
                        <div className="product-version-list">
                            {platformVersions.map((platform) => (
                                <div key={platform.id} className="product-version-row">
                                    <span className="product-icon">
                                        <platform.icon />
                                    </span>
                                    <div className="product-version-copy">
                                        <h3>{platform.label}</h3>
                                        <p>{platform.description}</p>
                                    </div>
                                    <ProductVersion
                                        version={platform.version}
                                        loading={platform.loading}
                                        label={`${platform.label} version`}
                                    />
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
                <Card className="product-panel">
                    <CardContent className="product-panel-content">
                        <div className="product-section-heading">
                            <span className="product-icon">
                                <Monitor />
                            </span>
                            <div>
                                <h2>Agent versions</h2>
                                <p>WhiteSwanSecurity across your operating systems.</p>
                            </div>
                        </div>
                        <div className="product-version-list">
                            {agentVersions.map((agent) => (
                                <div key={agent.id} className="product-version-row">
                                    <span className="product-icon">
                                        <agent.icon />
                                    </span>
                                    <div className="product-version-copy">
                                        <h3>{agent.label}</h3>
                                        <p>{agent.description}</p>
                                    </div>
                                    <ProductVersion
                                        version={agent.version}
                                        loading={agent.loading}
                                        label={`${agent.label} agent version`}
                                    />
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
            <Card className="product-panel company-panel">
                <CardContent className="product-panel-content">
                    <div className="product-section-heading">
                        <span className="product-icon">
                            <ExternalLink />
                        </span>
                        <div>
                            <h2>Connect with WhiteSwan</h2>
                            <p>Learn about the company, find us online, or get in touch.</p>
                        </div>
                    </div>
                    <div className="company-links">
                        {companyLinks.map((link) => (
                            <Button
                                key={link.id}
                                variant="outline"
                                onClick={() => handleLinkClick(link.label, link.url)}
                                className="company-link"
                                data-testid={`link-${link.id}`}
                                aria-label={`${link.label}, opens in a new tab`}
                            >
                                <span className="product-icon">
                                    <link.icon />
                                </span>
                                <span className="company-link-copy">
                                    <span>{link.label}</span>
                                    <span>{link.description}</span>
                                </span>
                                <ArrowUpRight aria-hidden="true" />
                            </Button>
                        ))}
                    </div>
                </CardContent>
            </Card>
            <ProductInfoGuide
                title="Understanding version information"
                description="Use these details when reviewing your deployment or contacting support."
                icon={Info}
                items={[
                    {
                        title: "Web console",
                        description: "The version of the WhiteSwan management interface you are using.",
                        icon: Code,
                    },
                    {
                        title: "Backend",
                        description: "The build version reported by the platform services for this deployment.",
                        icon: Server,
                    },
                    {
                        title: "Agent releases",
                        description: "The latest available agent release for each operating system. Installed versions are shown in Endpoints.",
                        icon: Monitor,
                    },
                ]}
            />
            <footer className="product-info-footer">
                © 2026 Whiteswan Security Inc. All rights reserved.
            </footer>
        </div>
    );
}
